import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, ShieldCheck, ArrowLeft } from "lucide-react";
import api from "../utils/api";
import { useBranding } from "../context/BrandingContext.jsx";
import { useLocale } from "../i18n";
import { Slot } from "../themes/ThemeContext.jsx";

// Certificado imprimible. Dos modos:
//  - /certificate/:code (sesión): detalle completo + marca de la organización.
//  - /verify/:code (público): solo lo que devuelve /certificates/verify.
// Los temas pueden reemplazar la lámina con Slot "Certificate" y reciben
// {certificate, org, publicMode}.

const STATUS_KEY = { valid: "cert.valid", expiring: "cert.expiring", expired: "cert.expired", revoked: "cert.expired" };

const fmtDate = (value, locale) => {
  if (!value) return "";
  try {
    return new Date(value).toLocaleDateString(locale, { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return String(value).slice(0, 10);
  }
};

export const CertificateSheet = ({ certificate, org, t, locale }) => {
  const branding = org?.branding || {};
  const primary = branding.primaryColor || "#28244a";
  const accent = branding.accentColor || branding.secondaryColor || "#2692d6";
  const font = branding.fontFamily ? `'${branding.fontFamily}', Inter, sans-serif` : undefined;
  const logo = org?.logo || org?.image;
  const verifyUrl = `${window.location.origin}/verify/${certificate.code}`;

  return (
    <div
      className="certificate-sheet mx-auto w-full max-w-[920px] bg-white text-slate-900 shadow-xl print:shadow-none"
      style={{ fontFamily: font, borderTop: `14px solid ${primary}` }}
      data-testid="certificate-sheet"
    >
      <div className="px-6 py-8 sm:px-12 sm:py-12">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {logo ? (
            <img src={logo} alt={org?.name || ""} className="h-12 w-auto max-w-[200px] object-contain sm:h-16" />
          ) : (
            <div className="text-xl font-black" style={{ color: primary }}>
              {org?.name}
            </div>
          )}
          <div className="text-right text-xs uppercase tracking-[0.25em] text-slate-500">{t("cert.title")}</div>
        </div>

        <div className="mt-10 text-center sm:mt-14">
          <p className="text-sm uppercase tracking-[0.2em] text-slate-500">{t("cert.certifies")}</p>
          <h1 className="mt-3 text-3xl font-black sm:text-5xl" style={{ color: primary }}>
            {certificate.userName}
          </h1>
          <p className="mt-5 text-base text-slate-600 sm:text-lg">{t("cert.completed")}</p>
          <h2 className="mt-2 text-xl font-bold sm:text-3xl">{certificate.title}</h2>
          {certificate.challengeName && certificate.challengeName !== certificate.title && (
            <p className="mt-1 text-sm text-slate-500">{certificate.challengeName}</p>
          )}
          {typeof certificate.score === "number" && (
            <p className="mt-4 inline-block rounded-full px-4 py-1 text-sm font-bold text-white" style={{ backgroundColor: accent }}>
              {t("cert.score", { pct: certificate.score })}
            </p>
          )}
        </div>

        <div className="mt-10 grid gap-6 border-t border-slate-200 pt-6 sm:mt-14 sm:grid-cols-3">
          <div className="text-sm text-slate-600">
            <div>{t("cert.issuedOn", { date: fmtDate(certificate.issuedAt, locale) })}</div>
            {certificate.expiresAt && <div className="mt-1">{t("cert.validUntil", { date: fmtDate(certificate.expiresAt, locale) })}</div>}
          </div>
          <div className="text-center text-sm">
            {branding.certificateSignatureUrl && (
              <img src={branding.certificateSignatureUrl} alt="" className="mx-auto h-14 object-contain" />
            )}
            <div className="mx-auto mt-1 w-40 border-t border-slate-400" />
            <div className="mt-1 font-semibold">{branding.certificateSignerName || org?.name}</div>
            <div className="text-xs text-slate-500">{branding.certificateSignerTitle || t("cert.signedBy")}</div>
          </div>
          <div className="text-sm text-slate-600 sm:text-right">
            {branding.certificateSealUrl && (
              <img src={branding.certificateSealUrl} alt="" className="ml-auto mb-2 h-16 object-contain" />
            )}
            <div className="text-xs uppercase tracking-wider text-slate-500">{t("cert.code")}</div>
            <div className="font-mono text-base font-bold">{certificate.code}</div>
            <div className="mt-1 text-xs text-slate-500">{t("cert.verify", { url: verifyUrl.replace(/^https?:\/\//, "") })}</div>
          </div>
        </div>
        {branding.certificateFooter && (
          <p className="mt-6 text-center text-xs text-slate-500">{branding.certificateFooter}</p>
        )}
      </div>
    </div>
  );
};

const CertificateView = ({ publicMode = false }) => {
  const { code } = useParams();
  const { org } = useBranding();
  const { t, locale } = useLocale();
  const [certificate, setCertificate] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setCertificate(null);
    setError(null);
    api
      .get(publicMode ? `/certificates/verify/${code}` : `/certificates/${code}`)
      .then((res) => !cancelled && setCertificate(res.data))
      .catch((err) => !cancelled && setError(err.response?.data?.message || t("common.loading")));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, publicMode]);

  const shownOrg = certificate?.organization && typeof certificate.organization === "object" ? certificate.organization : org;
  const statusKey = certificate ? STATUS_KEY[certificate.status] || "cert.valid" : null;
  const ok = certificate && (certificate.status === "valid" || certificate.status === "expiring");

  const body = error ? (
    <div className="mx-auto max-w-xl rounded-3xl border border-red-400/30 bg-red-950/30 p-8 text-center text-white">{error}</div>
  ) : !certificate ? (
    <div className="py-20 text-center text-slate-300">{t("common.loading")}</div>
  ) : (
    <CertificateSheet certificate={certificate} org={shownOrg} t={t} locale={locale} />
  );

  return (
    <div className="min-h-[70vh] px-4 py-6 sm:py-10 print:p-0" style={{ fontFamily: "var(--brand-font, inherit)" }}>
      <div className="mx-auto mb-4 flex w-full max-w-[920px] flex-wrap items-center justify-between gap-3 print:hidden">
        {publicMode ? (
          <div className="flex items-center gap-2 text-sm text-slate-200">
            <ShieldCheck className={`h-5 w-5 ${ok ? "text-green-400" : "text-amber-400"}`} />
            {certificate ? (
              <span>
                {typeof certificate.organization === "string" ? `${certificate.organization} · ` : ""}
                {t(statusKey)}
              </span>
            ) : null}
          </div>
        ) : (
          <Link to="/profile" className="inline-flex items-center gap-2 text-sm text-slate-200 hover:text-white">
            <ArrowLeft className="h-4 w-4" /> {t("cert.mine")}
          </Link>
        )}
        {certificate && (
          <div className="flex items-center gap-2">
            {!publicMode && (
              <span className={`rounded-full px-3 py-1 text-xs font-bold ${ok ? "bg-green-500/20 text-green-200" : "bg-amber-500/20 text-amber-200"}`}>
                {t(statusKey)}
              </span>
            )}
            <button type="button" onClick={() => window.print()} className="btn-brand inline-flex min-h-[40px] items-center gap-2 rounded-xl px-4 text-sm font-bold">
              <Printer className="h-4 w-4" /> {t("cert.print")}
            </button>
          </div>
        )}
      </div>
      {certificate && !publicMode ? (
        <Slot name="Certificate" certificate={certificate} org={shownOrg} publicMode={publicMode} fallback={body} />
      ) : (
        body
      )}
    </div>
  );
};

export default CertificateView;
